import { FormEvent, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { BlankInventoryDownload } from "./BlankInventoryDownload";
import { BrcPrimaryLogo, ErrorNotice, LoadingScreen, PathwayMark } from "./components";
import type {
  AdminParticipantListItem,
  AdminSummary,
  InventoryContent,
  Participant,
} from "./types";
type Filters = {
  search: string;
  participantType: string;
  pathway: string;
  status: string;
};

type ArchiveTarget = {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  archivedAt?: string | null;
};

const emptyFilters: Filters = { search: "", participantType: "", pathway: "", status: "" };

export function AdminApp() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [content, setContent] = useState<InventoryContent | null>(null);
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [participants, setParticipants] = useState<AdminParticipantListItem[]>([]);
  const [archivedParticipants, setArchivedParticipants] = useState<AdminParticipantListItem[]>([]);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [selected, setSelected] = useState<Participant | null>(null);
  const [printout, setPrintout] = useState(false);
  const [openingId, setOpeningId] = useState("");
  const [loading, setLoading] = useState(false);
  const [mutatingId, setMutatingId] = useState("");
  const [copyingResumeId, setCopyingResumeId] = useState("");
  const [resumeNotice, setResumeNotice] = useState("");
  const [resumeUrl, setResumeUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    async function initialize() {
      try {
        const loadedContent = await api.content();
        setContent(loadedContent);
        await api.admin.session();
        setAuthenticated(true);
      } catch {
        setAuthenticated(false);
      }
    }
    void initialize();
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    void refresh();
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated || !summary) return;
    const timeout = window.setTimeout(() => void loadParticipants(), 220);
    return () => window.clearTimeout(timeout);
  }, [filters, authenticated]);

  useEffect(() => {
    if (!authenticated || !summary) return;
    const query = new URLSearchParams(window.location.search);
    const requested = query.get("participant");
    if (requested) void openParticipant(requested, query.get("print") === "1");
  }, [authenticated, summary]);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const [nextSummary, nextParticipants, nextArchivedParticipants] = await Promise.all([
        api.admin.summary(),
        api.admin.participants(buildQuery(filters)),
        api.admin.archivedParticipants(),
      ]);
      setSummary(nextSummary);
      setParticipants(nextParticipants);
      setArchivedParticipants(nextArchivedParticipants);
    } catch (caught) {
      const message = messageOf(caught);
      if (message.toLowerCase().includes("sign in") || message.toLowerCase().includes("session")) {
        setAuthenticated(false);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadParticipants() {
    try {
      setParticipants(await api.admin.participants(buildQuery(filters)));
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  async function openParticipant(id: string, printable = false) {
    setOpeningId(id);
    setError("");
    setResumeNotice("");
    setResumeUrl("");
    try {
      setSelected(await api.admin.participant(id));
      setPrintout(printable);
      const url = new URL(window.location.href);
      url.searchParams.set("participant", id);
      if (printable) url.searchParams.set("print", "1");
      else url.searchParams.delete("print");
      window.history.replaceState({}, "", url);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setOpeningId("");
    }
  }

  function changePrintout(printable: boolean) {
    setPrintout(printable);
    const url = new URL(window.location.href);
    if (printable) url.searchParams.set("print", "1");
    else url.searchParams.delete("print");
    window.history.replaceState({}, "", url);
  }

  function closeParticipant() {
    setSelected(null);
    setPrintout(false);
    setResumeNotice("");
    setResumeUrl("");
    const url = new URL(window.location.href);
    url.searchParams.delete("participant");
    url.searchParams.delete("print");
    window.history.replaceState({}, "", url);
  }

  async function changeArchiveState(participant: ArchiveTarget, archive: boolean) {
    const name = participant.name || `${participant.firstName || ""} ${participant.lastName || ""}`.trim() || "this participant";
    if (archive && !window.confirm(`Archive ${name}? The result will move out of active analytics and into the Archived results section.`)) return;
    setMutatingId(participant.id);
    setError("");
    try {
      if (archive) await api.admin.archiveParticipant(participant.id);
      else await api.admin.unarchiveParticipant(participant.id);
      if (selected?.id === participant.id) closeParticipant();
      await refresh();
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setMutatingId("");
    }
  }

  async function copyResumeLink(participant: Participant) {
    setCopyingResumeId(participant.id);
    setResumeNotice("");
    setError("");
    try {
      const { url } = await api.admin.resumeLink(participant.id);
      setResumeUrl(url);
      try {
        await copyText(url);
        setResumeNotice(`Continue link copied for ${participant.firstName} ${participant.lastName}.`);
      } catch {
        setResumeNotice("Continue link created. Copy it from the field below.");
      }
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setCopyingResumeId("");
    }
  }

  if (authenticated === null || !content) return <LoadingScreen message="Opening the admin workspace..." />;
  if (!authenticated) {
    return <AdminLogin onSuccess={() => setAuthenticated(true)} />;
  }
  if (!summary) return <LoadingScreen message="Loading participant records..." />;

  const selectedRecord = selected && <ParticipantDetail content={content} participant={selected} printout={printout} mutating={mutatingId === selected.id} copyingResumeLink={copyingResumeId === selected.id} resumeNotice={resumeNotice} resumeUrl={resumeUrl} onArchiveState={changeArchiveState} onCopyResumeLink={copyResumeLink} onPrint={() => changePrintout(true)} onClose={printout ? () => changePrintout(false) : closeParticipant} />;
  if (printout && selectedRecord) return selectedRecord;

  const exportUrl = `/api/admin/export.csv${buildQuery(filters)}`;
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <a href="/" className="admin-brand">
          <div className="admin-brand-logo">
            <BrcPrimaryLogo />
          </div>
          <span>Inventory Admin</span>
        </a>
        <nav><a className="active" href="#overview">Overview</a><a href="#participants">Participants</a><a href="#archived">Archived</a></nav>
        <button className="button text" onClick={async () => { await api.admin.logout(); setAuthenticated(false); }}>Sign out</button>
      </aside>
      <main className="admin-main">
        <header className="admin-topbar"><div><p className="eyebrow">Admin workspace</p><h1>Redemptive Builder Inventory</h1></div><div className="admin-actions"><button className="button secondary" disabled={loading} onClick={() => void refresh()}>{loading ? "Refreshing..." : "Refresh"}</button><a className="button" href={exportUrl}>Export CSV</a><BlankInventoryDownload content={content} /></div></header>
        <ErrorNotice message={error} />
        <section id="overview" className="metric-grid">
          <Metric label="Started" value={summary.totalStarted} detail="participant records" />
          <Metric label="Completed" value={summary.fullyCompleted} detail={`${Math.round(summary.completionRate * 100)}% completion rate`} />
          <Metric label="Average time" value={summary.averageCompletionMinutes == null ? "-" : `${Math.round(summary.averageCompletionMinutes)}m`} detail="among completed journeys" />
          <Metric label="Tied results" value={summary.tieResults} detail="co-primary pathways" />
        </section>
        <section className="admin-insights">
          <div className="admin-panel pathway-distribution"><div className="panel-heading"><div><p className="eyebrow">Pathway mix</p><h2>Primary pathways</h2></div><span>{summary.fullyCompleted} completed</span></div><Distribution values={summary.pathwayCounts} /></div>
          <div className="admin-panel funnel-panel"><div className="panel-heading"><div><p className="eyebrow">Journey progress</p><h2>Completion funnel</h2></div></div><Funnel summary={summary} /></div>
        </section>
        <section id="participants" className="admin-panel participant-panel">
          <div className="panel-heading"><div><p className="eyebrow">Participant records</p><h2>Every journey, together</h2></div><span>{participants.length} shown</span></div>
          <div className="admin-filters">
            <label className="search-field"><span className="sr-only">Search participants</span><input placeholder="Search name, email, organization, location..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} /></label>
            <select aria-label="Participant type" value={filters.participantType} onChange={(e) => setFilters({ ...filters, participantType: e.target.value })}><option value="">All participants</option><option>Student</option><option>Professional</option></select>
            <select aria-label="Pathway" value={filters.pathway} onChange={(e) => setFilters({ ...filters, pathway: e.target.value })}><option value="">All pathways</option>{Object.values(content.pathways).map((pathway) => <option key={pathway.name}>{pathway.name}</option>)}</select>
            <select aria-label="Completion status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">All statuses</option><option value="started">Started</option><option value="inventory_complete">Inventory complete</option><option value="journey_complete">Journey complete</option><option value="completed">Completed</option></select>
            {Object.values(filters).some(Boolean) && <button className="clear-filters" onClick={() => setFilters(emptyFilters)}>Clear</button>}
          </div>
          <div className="participant-table-wrap">
            <ParticipantTable participants={participants} mutatingId={mutatingId} openingId={openingId} onOpen={openParticipant} onPrint={(id) => openParticipant(id, true)} onArchiveState={changeArchiveState} />
            {!participants.length && <div className="empty-state"><h3>No participant records match.</h3><p>Try clearing one or more filters.</p></div>}
          </div>
        </section>
        <details id="archived" className="admin-panel archived-panel">
          <summary><div><p className="eyebrow">Archived results</p><h2>Set aside, not deleted</h2><span>Archived records do not affect active analytics or exports.</span></div><strong>{archivedParticipants.length} archived</strong></summary>
          <div className="archived-content">
            {archivedParticipants.length ? <div className="participant-table-wrap"><ParticipantTable participants={archivedParticipants} archived mutatingId={mutatingId} openingId={openingId} onOpen={openParticipant} onPrint={(id) => openParticipant(id, true)} onArchiveState={changeArchiveState} /></div> : <div className="empty-state compact"><h3>No archived results.</h3><p>Records you archive will remain safely available here.</p></div>}
          </div>
        </details>
      </main>
      {selectedRecord}
    </div>
  );
}

function ParticipantTable({ participants, archived = false, mutatingId, openingId, onOpen, onPrint, onArchiveState }: { participants: AdminParticipantListItem[]; archived?: boolean; mutatingId: string; openingId: string; onOpen: (id: string) => Promise<void>; onPrint: (id: string) => Promise<void>; onArchiveState: (participant: ArchiveTarget, archive: boolean) => Promise<void> }) {
  return <table className="participant-table"><thead><tr><th>Participant</th><th>Type / organization</th><th>Pathway</th><th>Status</th><th>{archived ? "Archived" : "Started"}</th><th><span className="sr-only">Record actions</span></th></tr></thead><tbody>
    {participants.map((participant) => <tr key={participant.id} onClick={() => void onOpen(participant.id)}><td data-label="Participant"><strong>{participant.name}</strong><span>{participant.email}</span></td><td data-label="Type / organization"><strong>{participant.participantType}</strong><span>{participant.organization}</span></td><td data-label="Pathway">{participant.primaryPathways.length ? <div className="table-pathway"><PathwayMark name={participant.primaryPathways[0]} /><span>{participant.primaryPathways.join(" + ")}</span></div> : <span className="muted">Not calculated</span>}</td><td data-label="Status"><StatusBadge status={participant.status} /></td><td data-label={archived ? "Archived" : "Started"}>{formatDate((archived ? participant.archivedAt : participant.startedAt) || participant.startedAt)}</td><td><div className="table-actions"><button className="open-record" onClick={(event) => { event.stopPropagation(); void onOpen(participant.id); }} aria-label={`Open ${participant.name}`}>→</button><button className="print-record" disabled={Boolean(openingId)} onClick={(event) => { event.stopPropagation(); void onPrint(participant.id); }} aria-label={`Print ${participant.name}`}>{openingId === participant.id ? "Opening..." : "Print"}</button><button className="archive-record" disabled={mutatingId === participant.id} onClick={(event) => { event.stopPropagation(); void onArchiveState(participant, !archived); }}>{mutatingId === participant.id ? "Saving..." : archived ? "Unarchive" : "Archive"}</button></div></td></tr>)}
  </tbody></table>;
}

function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try { await api.admin.login(username, password); onSuccess(); }
    catch (caught) { setError(messageOf(caught)); }
    finally { setBusy(false); }
  }
  return (
    <div className="admin-login">
      <div className="login-art">
        <div className="login-art-logo">
          <BrcPrimaryLogo />
        </div>
        <p>Every participant's complete journey, thoughtfully organized.</p>
      </div>
      <form onSubmit={submit}>
        <p className="eyebrow">Private workspace</p>
        <h1>Welcome back.</h1>
        <p>Sign in to review participant responses and journey progress.</p>
        <label>Username<input required autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} /></label>
        <label>Password<input required type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        <ErrorNotice message={error} />
        <button className="button" disabled={busy}>{busy ? "Signing in..." : "Sign in →"}</button>
        <a href="/">Return to the Inventory</a>
      </form>
    </div>
  );
}

function Metric({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return <article className="metric-card"><p>{label}</p><strong>{value}</strong><span>{detail}</span></article>;
}

function Distribution({ values }: { values: Record<string, number> }) {
  const maximum = Math.max(1, ...Object.values(values));
  if (!Object.keys(values).length) return <p className="muted">Pathway distribution will appear after the first completed journey.</p>;
  return <div className="distribution-list">{Object.entries(values).sort((a, b) => b[1] - a[1]).map(([name, value]) => <div key={name}><span>{name}</span><div><i style={{ width: `${(value / maximum) * 100}%` }} /></div><strong>{value}</strong></div>)}</div>;
}

function Funnel({ summary }: { summary: AdminSummary }) {
  const steps = [["Started", summary.totalStarted], ["Inventory", summary.inventoryCompleted], ["Builder Journey", summary.journeyCompleted], ["Summary", summary.fullyCompleted]] as const;
  const maximum = Math.max(1, summary.totalStarted);
  return <div className="funnel-list">{steps.map(([label, value], index) => <div key={label}><span>{index + 1}</span><p>{label}<strong>{value}</strong></p><i style={{ width: `${(value / maximum) * 100}%` }} /></div>)}</div>;
}

function ParticipantDetail({ content, participant, printout, mutating, copyingResumeLink, resumeNotice, resumeUrl, onArchiveState, onCopyResumeLink, onPrint, onClose }: { content: InventoryContent; participant: Participant; printout: boolean; onPrint: () => void; mutating: boolean; copyingResumeLink: boolean; resumeNotice: string; resumeUrl: string; onArchiveState: (participant: ArchiveTarget, archive: boolean) => Promise<void>; onCopyResumeLink: (participant: Participant) => Promise<void>; onClose: () => void }) {
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (printout) {
      closeButton.current?.focus();
      return;
    }
    const scrollPosition = window.scrollY;
    const previousBodyStyles = document.body.style.cssText;
    const previousDocumentStyles = document.documentElement.style.cssText;
    const handler = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.body.style.position = "fixed";
    document.body.style.inset = `-${scrollPosition}px 0 0`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", handler);
    closeButton.current?.focus();
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.cssText = previousBodyStyles;
      document.documentElement.style.cssText = previousDocumentStyles;
      window.scrollTo(0, scrollPosition);
    };
  }, [onClose, printout]);
  const record = <><header><div><p className="eyebrow">{participant.archivedAt ? "Archived participant record" : "Participant record"}</p><h2 id="participant-detail-title">{participant.firstName} {participant.lastName}</h2><a href={`mailto:${participant.email}`}>{participant.email}</a></div>{!printout && <div className="detail-header-actions no-print"><button className="button secondary" onClick={onPrint}>Print record</button><button className="button secondary" disabled={mutating} onClick={() => void onArchiveState(participant, !participant.archivedAt)}>{mutating ? "Saving..." : participant.archivedAt ? "Unarchive" : "Archive record"}</button><button ref={closeButton} className="detail-close" onClick={onClose} aria-label="Close participant record">×</button></div>}</header><div className="detail-body">
    <section className="detail-summary-grid"><div><span>Status</span><StatusBadge status={participant.status} /></div><div><span>Started</span><strong>{formatDateTime(participant.startedAt)}</strong></div><div><span>Completed</span><strong>{participant.completedAt ? formatDateTime(participant.completedAt) : "Not yet"}</strong></div><div><span>Results email</span><strong>{humanize(participant.emailStatus)}</strong></div>{participant.archivedAt && <div><span>Archived</span><strong>{formatDateTime(participant.archivedAt)}</strong></div>}</section>
    {!printout && participant.status !== "completed" && !participant.archivedAt && <section className="resume-link-card"><div><p className="eyebrow">Continue an unfinished Inventory</p><h3>Send them back to exactly where they stopped.</h3><p>The participant will confirm this record's email address before their saved answers and next question are restored. Creating a link replaces any earlier link for this record.</p></div><button className="button secondary" disabled={copyingResumeLink} onClick={() => void onCopyResumeLink(participant)}>{copyingResumeLink ? "Creating link..." : "Create & copy link"}</button>{resumeUrl && <label className="resume-link-field"><span>Continue link</span><input readOnly value={resumeUrl} onFocus={(event) => event.currentTarget.select()} /></label>}{resumeNotice && <p className="resume-link-notice" role="status">{resumeNotice}</p>}</section>}
    {printout && <section className="printout-result">
      <p className="eyebrow">{participant.tieCount > 1 ? "Co-primary pathways" : "Primary pathway"}</p>
      {participant.primaryPathways.length ? participant.primaryPathways.map((name) => <div key={name}><PathwayMark name={name} /><div><h3>{name}</h3><p>{Object.values(content.pathways).find((pathway) => pathway.name === name)?.description}</p></div></div>) : <p>Not calculated. The Inventory has not been completed.</p>}
      {participant.status !== "completed" && <p className="printout-incomplete">Incomplete record. Only saved responses are shown; unanswered questions are marked below.</p>}
    </section>}
    <DetailSection printout={printout} title="Participant information"><dl><div><dt>Type</dt><dd>{participant.participantType}</dd></div><div><dt>Company / school</dt><dd>{participant.organization}</dd></div><div><dt>Profession / field</dt><dd>{participant.profession || "Not supplied"}</dd></div><div><dt>Town of residence</dt><dd>{participant.townOfResidence || "Not supplied"}</dd></div><div><dt>County of residence</dt><dd>{participant.countyOfResidence || "Not supplied"}</dd></div><div><dt>State of residence</dt><dd>{participant.stateOfResidence || "Not supplied"}</dd></div><div><dt>Church you attend</dt><dd>{participant.churchYouAttend || "Not supplied"}</dd></div><div><dt>Source</dt><dd>{participant.source || "Direct"}</dd></div><div><dt>Privacy</dt><dd>{participant.privacyAcknowledged ? "Acknowledged" : "Not acknowledged"}</dd></div></dl></DetailSection>
    <DetailSection printout={printout} title="Inventory and scoring"><div className="score-grid">{(Object.entries(content.pathways) as [string, { name: string }][]).map(([letter, pathway]) => <div className={participant.primaryPathways.includes(pathway.name) ? "primary" : ""} key={letter}><PathwayMark name={pathway.name} /><p>{pathway.name}<strong>{participant.scores[letter as keyof typeof participant.scores] || 0}</strong></p></div>)}</div>{participant.tieCount > 1 && <p className="tie-note">Co-primary tie: {participant.primaryPathways.join(" + ")}</p>}<div className="admin-answer-list">{content.questions.map((question) => { const answer = participant.inventoryAnswers[String(question.number)]; return <div key={question.number}><span>{question.number}</span><div><p>{question.text}</p><strong>{answer ? `${answer.letter}. ${answer.text}` : "Not answered"}</strong></div></div>; })}</div></DetailSection>
    <DetailSection printout={printout} title="Builder Journey"><div className="admin-reflection-list">{content.journeyQuestions.map((question) => { const answer = participant.journeyAnswers[String(question.number)]; return <div key={question.number}><p>{question.text}</p>{question.kind === "passions" ? <>{answer?.passions.length ? <ol>{answer.passions.map((passion, index) => <li key={index}>{passion}</li>)}</ol> : <strong>Not answered</strong>}</> : <strong>{answer?.text || "Not answered"}</strong>}</div>; })}</div></DetailSection>
    <DetailSection printout={printout} title="Historical feedback"><FeedbackDetail label="Pathway accuracy" value={participant.feedback.pathwayAccuracy} comment={participant.feedback.pathwayAccuracyComment} /><FeedbackDetail label="Intentionality / helpfulness" value={participant.feedback.helpfulness} comment={participant.feedback.helpfulnessComment} /><FeedbackDetail label="Confusing or unclear" value={participant.feedback.confusion} /><FeedbackDetail label="Desired additional insight" value={participant.feedback.desiredInsight} /><FeedbackDetail label="Additional comments" value={participant.feedback.additionalComments} /></DetailSection>
  </div></>;
  if (printout) return <main className="record-printout">
    <div className="record-printout-toolbar no-print">
      <div><h1>Record printout</h1><p>US Letter · 8.5 × 11 inches · Portrait</p></div>
      <div><button ref={closeButton} className="button secondary" onClick={onClose}>Back to record</button><button className="button" onClick={() => window.print()}>Print / save PDF</button></div>
    </div>
    <article className="participant-detail record-printout-document" aria-labelledby="participant-detail-title">
      <p className="record-printout-title">Redemptive Builder Inventory · Individual results</p>
      {record}
    </article>
  </main>;
  return <div className="detail-overlay" role="dialog" aria-modal="true" aria-labelledby="participant-detail-title"><button className="detail-scrim" aria-label="Close participant record" onClick={onClose} /><article className="participant-detail">{record}</article></div>;
}

function DetailSection({ title, children, printout }: React.PropsWithChildren<{ title: string; printout: boolean }>) {
  if (printout) {
    return <section className="detail-section"><h3 className="record-section-heading">{title}</h3><div className="detail-section-content">{children}</div></section>;
  }
  return <details className="detail-section" open><summary><h3>{title}</h3></summary><div className="detail-section-content">{children}</div></details>;
}
function FeedbackDetail({ label, value, comment }: { label: string; value: string; comment?: string }) { return <div className="feedback-detail"><p>{label}</p><strong>{value || "No response"}</strong>{comment && <span>{comment}</span>}</div>; }
function StatusBadge({ status }: { status: string }) { return <span className={`status-badge status-${status}`}>{humanize(status)}</span>; }
function humanize(value: string) { return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()); }
function formatDate(value: string) { return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value)); }
function formatDateTime(value: string) { return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
function buildQuery(filters: Filters) { const query = new URLSearchParams(); Object.entries(filters).forEach(([key, value]) => value && query.set(key, value)); const string = query.toString(); return string ? `?${string}` : ""; }
async function copyText(value: string) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const field = document.createElement("textarea");
  field.value = value;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.append(field);
  field.select();
  const copied = document.execCommand("copy");
  field.remove();
  if (!copied) throw new Error("The link was created, but your browser could not copy it. Please allow clipboard access and try again.");
}
function messageOf(error: unknown) { return error instanceof Error ? error.message : "Something went wrong. Please try again."; }
